import React, { useState } from 'react';

import { link, routes } from '@aline/routes';
import { ListItemIcon, ListItemText, Menu, MenuItem, Typography } from '@mui/material';
import type { GridToolbarProps } from '@mui/x-data-grid-premium';
import { FileEdit, Send } from 'lucide-react';
import { useMatch, useNavigate } from 'react-router-dom';
import {
  DropdownSelectButton,
  FiltersToolbarButton,
  SearchInput,
  Spacer,
  useListToolbarStyles,
} from 'src/components';
import { useDataGridSearch } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

import { NewTemplateButton } from '../NewTemplateButton';

const useStyles = makeStyles()(theme => ({
  menuItemIcon: {
    minWidth: `${theme.spacing(4)} !important`,
  },
  title: {
    flexShrink: 0,
    fontWeight: 600,
    marginRight: theme.spacing(1),
  },
}));

type TemplateFilterOption = 'draft' | 'published';

const TEMPLATE_FILTER_LABEL: Record<TemplateFilterOption, string> = {
  draft: 'Draft',
  published: 'Published',
};

const TEMPLATE_FILTER_ICON: Record<TemplateFilterOption, typeof Send> = {
  draft: FileEdit,
  published: Send,
};

export interface TemplateListToolbarProps extends GridToolbarProps {
  workflowCount?: number;
}

export function TemplateListToolbar(_props: TemplateListToolbarProps): JSX.Element | null {
  const { classes: toolbarClasses } = useListToolbarStyles();
  const { classes } = useStyles();
  const { search, setSearch } = useDataGridSearch();
  const navigate = useNavigate();
  const draftMatch = useMatch({ end: false, path: routes.TemplateDraft });
  const templateFilter: TemplateFilterOption = draftMatch ? 'draft' : 'published';
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);

  const selectTemplateFilter = (option: TemplateFilterOption) => {
    setFilterMenuOpen(false);
    navigate(option === 'draft' ? link.TemplateDraft() : link.TemplatePublished());
  };

  const TriggerIcon = TEMPLATE_FILTER_ICON[templateFilter];

  return (
    <div className={toolbarClasses.toolbar}>
      <Typography className={classes.title} variant="h6">
        Templates
      </Typography>
      <FiltersToolbarButton />
      <DropdownSelectButton
        ref={setFilterAnchorEl}
        icon={<TriggerIcon size={18} />}
        label={TEMPLATE_FILTER_LABEL[templateFilter]}
        selected={filterMenuOpen}
        variant="outlined"
        onClick={() => setFilterMenuOpen(!filterMenuOpen)}
      />
      <Menu
        anchorEl={filterAnchorEl}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        open={filterMenuOpen}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        onClose={() => setFilterMenuOpen(false)}
      >
        <MenuItem
          selected={templateFilter === 'published'}
          onClick={() => selectTemplateFilter('published')}
        >
          <ListItemIcon className={classes.menuItemIcon}>
            <Send size={18} />
          </ListItemIcon>
          <ListItemText>Published</ListItemText>
        </MenuItem>
        <MenuItem
          selected={templateFilter === 'draft'}
          onClick={() => selectTemplateFilter('draft')}
        >
          <ListItemIcon className={classes.menuItemIcon}>
            <FileEdit size={18} />
          </ListItemIcon>
          <ListItemText>Draft</ListItemText>
        </MenuItem>
      </Menu>
      <Spacer />
      <SearchInput placeholder="Search templates..." search={search} setSearch={setSearch} />
      <NewTemplateButton />
    </div>
  );
}
